import { useState, useEffect } from "react";
import { useIsMobile } from "../hooks/useIsMobile";
import Sidebar, { NAV_ITEMS } from "./layout/Sidebar";
import TopBar from "./layout/TopBar";
import BottomNav from "./layout/BottomNav";
import AddAccountModal from "./modals/AddAccountModal";
import LogoutConfirmModal from "./modals/LogoutConfirmModal";
import ReportsModal from "./modals/ReportsModal";
import TrialBanner from "./shared/TrialBanner";
import OverviewPage from "./pages/OverviewPage";
import AccountsPage from "./pages/AccountsPage";
import TransactionsPage from "./pages/TransactionsPage";
import BudgetPage from "./pages/BudgetPage";
import InvestmentsPage from "./pages/InvestmentsPage";
import AccountInfoPage from "./pages/AccountInfoPage";
import CalculatorsPage from "./pages/CalculatorsPage";
import BlogPage from "./pages/BlogPage";
import SupportPage from "./pages/SupportPage";
import AdminDashboard from "./pages/AdminDashboard";
import CoachChat from "./shared/CoachChat";
import { ACCOUNTS } from "../data/mockData";

const API = process.env.REACT_APP_API_URL || "http://localhost:4000";

// Valid page IDs — used to validate URL path on load
const VALID_PAGES = ["dashboard", "accounts", "transactions", "budget", "investments", "calculators", "blog", "support", "admin", "account-info"];

function pageFromPath() {
  const seg = window.location.pathname.replace(/^\//, "").split("/")[0];
  return VALID_PAGES.includes(seg) ? seg : "dashboard";
}

// Build planState from the /auth/me API response
function buildPlanState(user) {
  const status = user?.subscription_status;
  if (status === "active") {
    return { plan: "premium" };
  }
  if (status === "trialing" && user.trial_end) {
    const trialEnd  = new Date(user.trial_end);
    const now       = new Date();
    const daysLeft  = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
    const daysUsed  = Math.max(0, 14 - daysLeft);
    const trialEndStr = trialEnd.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
    return { plan: "trial", daysLeft, daysUsed, totalDays: 14, trialEnd: trialEndStr };
  }
  if (status === "canceled" || status === "past_due" || status === "unpaid") {
    return { plan: "expired" };
  }
  // No subscription row at all — treat as fresh trial
  return { plan: "trial", daysLeft: 14, daysUsed: 0, totalDays: 14, trialEnd: "" };
}

export default function FinanceDashboard({ token, onLogout }) {
  const [activeNav,         setActiveNav]         = useState(pageFromPath);
  const [accountFilter,     setAccountFilter]     = useState("all");
  const [mounted,           setMounted]           = useState(false);
  const [showAddAccount,    setShowAddAccount]    = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [showReports,       setShowReports]       = useState(false);
  const [planState,         setPlanState]         = useState({ plan: "loading" });
  const [plaidLoading,      setPlaidLoading]      = useState(false);
  const [plaidStatus,       setPlaidStatus]       = useState(null);
  const [userRole,          setUserRole]          = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const isMobile = useIsMobile();
  const [totpEnabled,       setTotpEnabled]       = useState(false);

  // Fetch user profile + live subscription status from the backend
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/v1/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => {
        if (!d) return;
        if (d.role) setUserRole(d.role);
        if (typeof d.totp_enabled === 'boolean') setTotpEnabled(d.totp_enabled);
        setPlanState(buildPlanState(d));
      })
      .catch(() => setPlanState({ plan: "trial", daysLeft: 14, daysUsed: 0, totalDays: 14, trialEnd: "" }));
  }, [token]);

  // Sync URL → activeNav when user hits back/forward
  useEffect(() => {
    const onPop = () => setActiveNav(pageFromPath());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  // Sync activeNav → URL whenever the page changes
  useEffect(() => {
    const path = activeNav === "dashboard" ? "/" : `/${activeNav}`;
    if (window.location.pathname !== path) {
      window.history.pushState({ page: activeNav }, "", path);
    }
    document.title = activeNav === "account-info"
      ? "Account Settings — Ledger"
      : `${NAV_ITEMS.find(n => n.id === activeNav)?.label ?? "Dashboard"} — Ledger`;
  }, [activeNav]);

  // Strip ?session_id= from URL after Stripe checkout redirect
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.has('session_id')) {
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  const handleUpgrade = () => {
    // Optimistically update while Stripe checkout completes
    setPlanState({ plan: "premium" });
  };

  useEffect(() => { setTimeout(() => setMounted(true), 100); }, []);

  // ── Manual investments total (kept in sync with InvestmentsPage localStorage) ─
  const calcManualInvTotal = () => {
    try {
      const items = JSON.parse(localStorage.getItem("ledger_manual_investments") || "[]");
      return items.reduce((sum, inv) => {
        const n = (k) => parseFloat(inv[k] || 0);
        switch (inv.type) {
          case "stock": case "etf":  return sum + n("shares")   * n("currentPrice");
          case "bond":               return sum + n("currentValue");
          case "mutualfund":         return sum + n("units")    * n("nav");
          case "cash":               return sum + n("amount");
          case "realestate":         return sum + n("currentValue");
          case "alternative":        return sum + n("quantity") * n("currentPrice");
          default: return sum;
        }
      }, 0);
    } catch { return 0; }
  };

  const [manualInvTotal, setManualInvTotal] = useState(calcManualInvTotal);

  // Re-sync whenever InvestmentsPage modifies manual investments
  useEffect(() => {
    const onUpdate = () => setManualInvTotal(calcManualInvTotal());
    window.addEventListener("manualInvestmentsChanged", onUpdate);
    return () => window.removeEventListener("manualInvestmentsChanged", onUpdate);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived values ─────────────────────────────────────────────────────────
  const totalAssets    = ACCOUNTS.filter(a => a.balance > 0).reduce((s, a) => s + a.balance, 0) + manualInvTotal;
  const totalDebt      = ACCOUNTS.filter(a => a.balance < 0).reduce((s, a) => s + a.balance, 0);
  const netWorth       = totalAssets + totalDebt;
  const monthlyChange  = ACCOUNTS.reduce((s, a) => s + a.change, 0);
  const filteredAccounts = accountFilter === "all"
    ? ACCOUNTS
    : ACCOUNTS.filter(a => a.type === accountFilter);

  // ── Plaid link-token flow ──────────────────────────────────────────────────
  const startPlaidLink = async () => {
    setPlaidLoading(true);
    setPlaidStatus(null);
    try {
      const res  = await fetch(`${API}/api/v1/plaid/link-token`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.link_token) {
        setPlaidStatus("✅ Link token obtained. Plaid flow would open here.");
      } else {
        setPlaidStatus("⚠️ " + (data.error || "Failed to get link token."));
      }
    } catch (e) {
      setPlaidStatus("⚠️ Could not reach backend: " + e.message);
    } finally {
      setPlaidLoading(false);
    }
  };

  const handleAddAccountClose = () => { setShowAddAccount(false); setPlaidStatus(null); };

  // Page title — account-info has its own label not in NAV_ITEMS
  const pageTitle = activeNav === "account-info"
    ? "Account Settings"
    : (NAV_ITEMS.find(n => n.id === activeNav)?.label ?? "Overview");

  return (
    <>
    <div style={{
      minHeight: "100vh", background: "var(--bg-main)", color: "var(--text-primary)",
      fontFamily: "'Capsule Sans', sans-serif",
      opacity: mounted ? 1 : 0, transition: "opacity 0.6s ease",
    }}>
      {/* Google Fonts + global resets */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@300;400;500;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: var(--bg-main); }
        ::-webkit-scrollbar-thumb { background: var(--border-strong); border-radius: 2px; }
        body { background: var(--bg-main); }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(16px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes pulse-dot {
          0%, 100% { opacity: 1; }
          50%       { opacity: 0.3; }
        }
        .card-anim { animation: slideUp 0.5s ease forwards; opacity: 0; }
        .card-anim:nth-child(1) { animation-delay: 0.05s; }
        .card-anim:nth-child(2) { animation-delay: 0.1s;  }
        .card-anim:nth-child(3) { animation-delay: 0.15s; }
        .card-anim:nth-child(4) { animation-delay: 0.2s;  }
      `}</style>

      <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
        <Sidebar
          activeNav={activeNav}
          onNavChange={setActiveNav}
          isAdmin={userRole === "admin"}
          planState={planState}
          mobileOpen={mobileSidebarOpen}
          onMobileClose={() => setMobileSidebarOpen(false)}
        />

        <main style={{ flex: 1, overflow: "auto", display: "flex", flexDirection: "column" }}>
          <TopBar
            pageTitle={pageTitle}
            planState={planState}
            onAddAccount={() => setShowAddAccount(true)}
            onLogout={() => setShowLogoutConfirm(true)}
            onAccountInfo={() => setActiveNav("account-info")}
            onExportReports={() => setShowReports(true)}
            onSidebarToggle={() => setMobileSidebarOpen(o => !o)}
          />

          {planState.plan === "trial" && (
            <TrialBanner
              daysLeft={planState.daysLeft}
              totalDays={planState.totalDays}
              trialEnd={planState.trialEnd}
              onUpgrade={() => setActiveNav("account-info")}
            />
          )}

          <div className="page active" style={{ padding: (activeNav === "admin" || activeNav === "support") ? "0" : isMobile ? "16px" : "28px 32px", flex: 1, paddingBottom: isMobile ? "80px" : ((activeNav === "admin" || activeNav === "support") ? "0" : "28px") }}>
            {activeNav === "dashboard"    && <OverviewPage netWorth={netWorth} totalAssets={totalAssets} totalDebt={totalDebt} monthlyChange={monthlyChange} />}
            {activeNav === "accounts"     && <AccountsPage accounts={filteredAccounts} filter={accountFilter} onFilterChange={setAccountFilter} totalAssets={totalAssets} totalDebt={totalDebt} netWorth={netWorth} />}
            {activeNav === "transactions" && <TransactionsPage />}
            {activeNav === "budget"       && <BudgetPage />}
            {activeNav === "investments"  && <InvestmentsPage />}
            {activeNav === "calculators"  && <CalculatorsPage />}
            {activeNav === "blog"         && <BlogPage />}
            {activeNav === "support"      && <SupportPage token={token} />}
            {activeNav === "admin"        && userRole === "admin" && <AdminDashboard token={token} />}
            {activeNav === "account-info" && <AccountInfoPage planState={planState} onUpgrade={handleUpgrade} token={token} totpEnabled={totpEnabled} onTotpChange={setTotpEnabled} />}
          </div>
        </main>
      </div>

      {/* Add Account modal */}
      {showAddAccount && (
        <AddAccountModal
          onClose={handleAddAccountClose}
          plaidLoading={plaidLoading}
          plaidStatus={plaidStatus}
          onConnect={startPlaidLink}
        />
      )}

      {/* Logout confirmation modal */}
      {showLogoutConfirm && (
        <LogoutConfirmModal
          onConfirm={onLogout}
          onCancel={() => setShowLogoutConfirm(false)}
        />
      )}

      {/* Reports modal */}
      {showReports && (
        <ReportsModal onClose={() => setShowReports(false)} />
      )}

      {/* Bottom navigation bar (mobile only) */}
      <BottomNav activeNav={activeNav} onNavChange={setActiveNav} />
    </div>

    {/* AI Financial Coach — rendered outside the opacity wrapper so
        position:fixed is always relative to the true viewport */}
    <CoachChat token={token} />
    </>
  );
}
